import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { RadioCard } from '../../../../components/common/Radio/RadioCard';
import { IconButton } from '../../../../components/common/buttons/IconButton';
import { Icon } from '../../../../components/common/icon/Icon';
import { BasicInputWithExtras } from '../../../../components/common/inputs/BasicInputWithExtras';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputContainer } from '../../../../components/common/survey/inputs/SurveyInputContainer';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import {
  SELL_TRACTOR_OPTION_VALUES,
  sellTractorSurveyOptionConstants,
  sellTractorSurveySteps,
} from '../../../../constants/survey/sellTractorSurvey';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { InputValue, SurveyFormOptionDetail, SurveyFormOptionType } from '../../../../interfaces/Element.interfaces';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { SellTractorSurveyPageWrapper } from '../SellTractorSurveyPageWrapper';

export const SellTractorSurveyOptionPage = () => {
  const navigate = useNavigate();

  const placeholderOfSecondInput = '예) 가죽핸들';
  const defaultPlaceholder = '입력하기';
  const idPrefix = 'working-';
  const INITIAL_OPTION_DETAILS: SurveyFormOptionDetail[] = [
    { id: `${idPrefix}0`, value: '', placeholder: '예) 전방 PTO' },
    { id: `${idPrefix}1`, value: '', placeholder: placeholderOfSecondInput },
  ];
  const INITIAL_NEXT_OPTION_ID = 2;

  const [option, setOption] = useState<string>();
  const [optionDetails, setOptionDetails] = useState<SurveyFormOptionDetail[]>(INITIAL_OPTION_DETAILS);
  const [nextOptionId, setNextOptionId] = useState<number>(INITIAL_NEXT_OPTION_ID);
  const [isOptionInputVisible, setIsOptionInputVisible] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const userSellTractorSurveySelector = useAppSelector((state) => state.userSellTractorSurvey);
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;

  const currentStep = sellTractorSurveySteps.STEP2;
  const steps = Object.keys(sellTractorSurveySteps);

  const handleAddOption = () => {
    if (optionDetails.length >= 10) {
      return;
    }
    const placeholderText = optionDetails.length === 1 ? placeholderOfSecondInput : defaultPlaceholder;
    const nextOptionDetails: SurveyFormOptionDetail[] = optionDetails.concat({
      id: `${idPrefix}${nextOptionId}`,
      value: '',
      placeholder: placeholderText,
    });
    setOptionDetails(nextOptionDetails);
    setNextOptionId(nextOptionId + 1);
  };

  const handleOptionRemove = (id: string) => {
    setOptionDetails((prevDetails) => prevDetails.filter((option) => option.id !== id));
  };

  const handleOptionChange = (id: string, value: string) => {
    setOptionDetails((prevDetails) => {
      return prevDetails.map((option) => (option.id === id ? { ...option, value } : option));
    });
  };

  useEffect(() => {
    const { option, optionDetails } = userSellTractorSurveySelector;
    setOption(option);

    if (option !== SELL_TRACTOR_OPTION_VALUES.YES) {
      return;
    }

    setIsOptionInputVisible(true);

    if (optionDetails && optionDetails.length > 0) {
      let copiedOptionId = nextOptionId;

      const currentOptionDetails: SurveyFormOptionDetail[] = optionDetails.map((detail: string, index) => {
        switch (index) {
          case 0:
            return {
              ...INITIAL_OPTION_DETAILS[0],
              value: detail,
            };
          case 1:
            return {
              ...INITIAL_OPTION_DETAILS[1],
              value: detail,
            };
          default: {
            const convertedDetail: SurveyFormOptionDetail = {
              id: `${idPrefix}${copiedOptionId}`,
              value: detail,
              placeholder: defaultPlaceholder,
            };
            copiedOptionId += 1;
            return convertedDetail;
          }
        }
      });
      setOptionDetails(currentOptionDetails);
      setNextOptionId(copiedOptionId);
    }
  }, [userSellTractorSurveySelector.option]);

  return (
    <>
      <SellTractorSurveyPageWrapper
        headerTitle={'옵션이 있나요?'}
        canConfirm
        prevButtonHandler={() => navigate('/survey/progress/sell-tractor/required-input-overview', { replace: true })}
        confirmHandler={() => {
          if (option) {
            dispatch(userSellTractorSurveyActions.changeOption(option));
          }

          if (option === SELL_TRACTOR_OPTION_VALUES.YES && optionDetails && optionDetails.length > 0) {
            const convertedOptionDetails = optionDetails
              .filter((optionDetail) => !!optionDetail.value)
              .map((filteredOptionDetail) => filteredOptionDetail.value);
            dispatch(userSellTractorSurveyActions.changeOptionDetails(convertedOptionDetails));
          }

          navigate('/survey/progress/sell-tractor/working-machine', { replace: true });
        }}
        steps={steps}
        currentStep={currentStep}
      >
        <SurveyForm padding={'0px 16px 24px 16px'}>
          <SurveyInputSection>
            <SurveyInputHeader inputTitle={'옵션 포함 유무'} isOptional />

            <SurveyInputContainer gridColumns={2}>
              {sellTractorSurveyOptionConstants.map(({ value, label }: SurveyFormOptionType) => (
                <RadioCard
                  key={value}
                  title={value}
                  label={label}
                  onClick={() => {
                    setOption(value);

                    if (value === SELL_TRACTOR_OPTION_VALUES.YES) {
                      setIsOptionInputVisible(true);
                      return;
                    }
                    setIsOptionInputVisible(false);
                    setOptionDetails(INITIAL_OPTION_DETAILS);
                    setNextOptionId(INITIAL_NEXT_OPTION_ID);
                  }}
                  isActive={option === value}
                />
              ))}
            </SurveyInputContainer>
          </SurveyInputSection>

          {isOptionInputVisible && (
            <>
              <SurveyInputSection>
                {optionDetails.map((optionDetail) => {
                  if (optionDetail.id === `${idPrefix}0`) {
                    return (
                      <BasicInputWithExtras
                        key={optionDetail.id}
                        inputLabel={'옵션 내역'}
                        inputValue={optionDetail.value}
                        placeholder={optionDetail.placeholder}
                        onChange={(value: InputValue) => handleOptionChange(optionDetail.id, value as string)}
                      />
                    );
                  }
                  return (
                    <BasicInputWithExtras
                      key={optionDetail.id}
                      inputValue={optionDetail.value}
                      placeholder={optionDetail.placeholder}
                      onChange={(value: InputValue) => handleOptionChange(optionDetail.id, value as string)}
                    >
                      <IconButton
                        onClickEvent={() => handleOptionRemove(optionDetail.id)}
                        icon={{ name: 'MinusCircle', width: 20, height: 20 }}
                      />
                    </BasicInputWithExtras>
                  );
                })}
              </SurveyInputSection>
              <AddItemButton onClick={handleAddOption} disabled={optionDetails.length >= 10}>
                <Icon name="Plus" width={14} height={14} />
                <AddItemButtonText>{'항목 추가'}</AddItemButtonText>
              </AddItemButton>
            </>
          )}
        </SurveyForm>
      </SellTractorSurveyPageWrapper>
    </>
  );
};

const SurveyInputSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const AddItemButton = styled.button`
  display: flex;
  max-height: 40px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
  &:disabled {
    opacity: 0.3;
  }
`;

const AddItemButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
